/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { EditorState, Modifier } from 'draft-js';

/**
 * Converts a leading dash followed by Space into an unordered list item.
 * Returns null when Draft should insert the input normally.
 *
 * @param {EditorState} editorState Current editor state.
 * @param {string} chars Pending input characters.
 * @return {?EditorState} Updated list state or null.
 */
function handleListShortcut(editorState, chars) {
  if (chars !== ' ') {
    return null;
  }

  const selection = editorState.getSelection();
  if (!selection.isCollapsed() || selection.getStartOffset() !== 1) {
    return null;
  }

  const content = editorState.getCurrentContent();
  const block = content.getBlockForKey(selection.getStartKey());
  if (block.getType() !== 'unstyled' || block.getText() !== '-') {
    return null;
  }

  const markerSelection = selection.merge({
    anchorOffset: 0,
    focusOffset: 1,
  });
  const contentWithoutMarker = Modifier.removeRange(
    content,
    markerSelection,
    'backward'
  );
  const listContent = Modifier.setBlockType(
    contentWithoutMarker,
    contentWithoutMarker.getSelectionAfter(),
    'unordered-list-item'
  );
  const nextState = EditorState.push(
    editorState,
    listContent,
    'change-block-type'
  );

  return EditorState.forceSelection(nextState, listContent.getSelectionAfter());
}

export default handleListShortcut;
